import * as React from 'react';
import { Dropdown, IDropdownOption, TextField } from 'office-ui-fabric-react';

import { ContractPartTimeSplitType, ContractPartTimeSplitTypes, ContractTypeModel } from '@app/Models';
import { EnumHelpers } from '@app/Services/EnumHelpers';

type ChangeBlockEventHandler = (value: number) => void;
type ChangeReasonEventHandler = (value?: string) => void;
type ChangeSplitTypeHandler = (type?: ContractPartTimeSplitType) => void;

type ContractPartTimeFixDayBlockProps = {
    onChangeContractDefinition: ChangeBlockEventHandler;
    onChangeSplitType: ChangeSplitTypeHandler;
    onChangeStartWeekPerContract: ChangeBlockEventHandler;
    onChangeDaysPerWeek: ChangeBlockEventHandler;
    onChangeFirstDay: ChangeBlockEventHandler;
    onChangeSecondDay: ChangeBlockEventHandler;
    onChangeReason: ChangeReasonEventHandler;
    contractDefinition?: number;
    startWeekPerContract?: number;
    daysPerWeek?: number;
    firstDay?: number;
    secondDay?: number;
    splitType?: ContractPartTimeSplitType;
    reason?: string;
    className?: string;
    disabled?: boolean;
    isCabin: boolean;
    typeModel: ContractTypeModel;
};

export class ContractPartTimeFixDayBlock extends React.PureComponent<ContractPartTimeFixDayBlockProps, {}> {
    private _store: ContractPartTimeFixDayBlockStore;

    constructor(props: ContractPartTimeFixDayBlockProps) {
        super(props);
        this._store = new ContractPartTimeFixDayBlockStore(props);
    }

    render() {
        return (
            <React.Fragment>
                {this.props.isCabin && (
                    <Dropdown
                        label="Contract Definition"
                        options={this._store.dayContractDefinitionOptions}
                        onChange={this._store.onChangeContractDefinition}
                        selectedKey={this.props.contractDefinition}
                        required
                        className={this.props.className}
                        disabled={this.props.disabled}
                    />
                )}
                {this.props.typeModel === ContractTypeModel.TZV_FixDay_Mff && (
                    <Dropdown
                        label="Week"
                        options={this._store.fixDayDaysPerWeekOptions}
                        onChange={this._store.onChangeStartWeekPerContract}
                        required={true}
                        selectedKey={this.props.startWeekPerContract}
                        className={this.props.className}
                        disabled={this.props.disabled}
                    />
                )}
                <Dropdown
                    label="Days Per Week "
                    options={this._store.fixDayDaysPerWeekOptions}
                    onChange={this._store.onChangeDaysPerWeek}
                    required={true}
                    selectedKey={this.props.daysPerWeek}
                    className={this.props.className}
                    disabled={this.props.disabled}
                />
                <Dropdown
                    label="Day 1"
                    options={this.props.typeModel === ContractTypeModel.TZV_FixDay ? this._store.fixDayDayOptions : this._store.fixDayShMffDayOptions}
                    onChange={this._store.onChangeFirstDay}
                    required={true}
                    selectedKey={this.props.firstDay}
                    className={this.props.className}
                    disabled={this.props.disabled}
                />
                {this.props.daysPerWeek === 2 &&
                    <Dropdown
                        label="Day2"
                        options={this.props.typeModel === ContractTypeModel.TZV_FixDay ? this._store.fixDayDayOptions : this._store.fixDayShMffDayOptions}
                        onChange={this._store.onChangeSecondDay}
                        required={true}
                        selectedKey={this.props.secondDay}
                        className={this.props.className}
                        disabled={this.props.disabled}
                    />
                }
                {this.props.daysPerWeek === 2 && this.props.firstDay && this.props.secondDay && this.props.firstDay === this.props.secondDay &&
                    <span className="ms-TextField-errorMessage errorMessage">Second Day is the same as First Day</span>
                }
                {this.props.isCabin && (
                    <Dropdown
                        label="Split Type"
                        options={this._store.contractSplitTypeOptions}
                        onChange={this._store.onChangeSplitType}
                        selectedKey={this.props.splitType as string}
                        className={this.props.className}
                        disabled={this.props.disabled}
                    />
                )}
                <TextField
                    label="Reason"
                    maxLength={300}
                    value={this.props.reason || ''}
                    onChange={this._store.onChangeReason}
                    className={this.props.className}
                    disabled={this.props.disabled}
                />
            </React.Fragment>
        );
    }
}

class ContractPartTimeFixDayBlockStore {
    private readonly _onChangeContractDefinitionFunction: ChangeBlockEventHandler;
    private readonly _onChangeSplitTypeFunction: ChangeSplitTypeHandler;
    private readonly _onChangeStartWeekPerContractFunction: ChangeBlockEventHandler;
    private readonly _onChangeDaysPerWeekFunction: ChangeBlockEventHandler;
    private readonly _onChangeFirstDayFunction: ChangeBlockEventHandler;
    private readonly _onChangeSecondDayFunction: ChangeBlockEventHandler;
    private readonly _onChangeReasonFunction: ChangeReasonEventHandler;

    constructor(props: ContractPartTimeFixDayBlockProps) {
        this._onChangeContractDefinitionFunction = props.onChangeContractDefinition;
        this._onChangeSplitTypeFunction = props.onChangeSplitType;
        this._onChangeStartWeekPerContractFunction = props.onChangeStartWeekPerContract;
        this._onChangeDaysPerWeekFunction = props.onChangeDaysPerWeek;
        this._onChangeFirstDayFunction = props.onChangeFirstDay;
        this._onChangeSecondDayFunction = props.onChangeSecondDay;
        this._onChangeReasonFunction = props.onChangeReason;
    }

    onChangeContractDefinition = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            this._onChangeContractDefinitionFunction(option.key as number);
        }
    };

    onChangeSplitType = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            this._onChangeSplitTypeFunction(option.key as ContractPartTimeSplitType || undefined);
        }
    };

    onChangeStartWeekPerContract = (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            this._onChangeStartWeekPerContractFunction(option.key as number);
        }
    };

    onChangeDaysPerWeek = (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            this._onChangeDaysPerWeekFunction(option.key as number);
        }
    };

    onChangeFirstDay = (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            this._onChangeFirstDayFunction(option.key as number);
        }
    };

    onChangeSecondDay = (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            this._onChangeSecondDayFunction(option.key as number);
        }
    };

    onChangeReason = (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        this._onChangeReasonFunction(newValue);
    };

    get dayContractDefinitionOptions() {
        return [
            { key: 40, text: '40' },
            { key: 50, text: '50' },
            { key: 60, text: '60' },
            { key: 70, text: '70' },
        ];
    }

    get fixDayDaysPerWeekOptions() {
        return [
            { key: 1, text: '1' },
            { key: 2, text: '2' },
        ];
    }

    get fixDayDayOptions() {
        return [
            { key: 1, text: '1' },
            { key: 2, text: '2' },
            { key: 3, text: '3' },
            { key: 4, text: '4' },
            { key: 5, text: '5' },
        ];
    }

    get fixDayShMffDayOptions() {
        return [
            { key: 1, text: '1' },
            { key: 2, text: '2' },
            { key: 3, text: '3' },
            { key: 4, text: '4' },
        ];
    }

    public get contractSplitTypeOptions() {
        return [{ key: '', text: '' }, ...EnumHelpers.toKeyValueArray(ContractPartTimeSplitTypes)];
    }
}